import { Link } from 'react-router-dom'
import type { Schwellen } from '../../hooks/uebersichtRegeln'
import type { UebersichtDaten } from '../../hooks/useUebersicht'
import { FaelligEintrag } from './FaelligEintrag'
import { Marktlage } from './Marktlage'
import { UebersichtEintrag } from './UebersichtEintrag'
import { UebersichtSpalte } from './UebersichtSpalte'
import s from './UebersichtAnsicht.module.scss'

/** Guten Morgen, Guten Tag, Guten Abend — nach der Uhr. */
export function gruss(jetzt: Date = new Date()): string {
  const stunde = jetzt.getHours()
  return stunde < 11 ? 'Guten Morgen' : stunde < 18 ? 'Guten Tag' : 'Guten Abend'
}

const uhr = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })

/** Die Zähler der Kopfzeile: neu, heute fällig, überfällig. */
export function zaehle(daten: UebersichtDaten) {
  const neu = [...daten.positiv, ...daten.negativ, ...daten.faellig]
    .filter((e) => e.neu).length
  return {
    neu,
    heuteFaellig: daten.faellig.filter((f) => f.heute).length,
    ueberfaellig: daten.faellig.filter((f) => f.ueberfaellig).length,
  }
}

/**
 * Die Übersicht selbst: Gruß und Zähler, die Marktlage, drei Spalten, die
 * Zeile „Ohne Vorzeichen".
 *
 * OHNE NETZ: Die Seite gibt die Daten aus dem Haken herein, die Vorlage im
 * Design-System Beispiele. `nurNeu` blendet aus, was nicht seit gestern
 * Abend dazukam — die Zahlen im Spaltenkopf bleiben dabei stehen.
 */
export function UebersichtAnsicht({ daten, nurNeu, schwellen, laedt }: {
  daten: UebersichtDaten
  nurNeu: boolean
  schwellen: Schwellen
  laedt?: boolean
}) {
  const { neu, heuteFaellig, ueberfaellig } = zaehle(daten)
  const sicht = <T extends { neu: boolean }>(liste: T[]) =>
    nurNeu ? liste.filter((e) => e.neu) : liste
  const positiv = sicht(daten.positiv)
  const negativ = sicht(daten.negativ)
  const faellig = sicht(daten.faellig)
  const neuText = (liste: { neu: boolean }[]) => {
    const n = liste.filter((e) => e.neu).length
    return <span className={s.neu}>{n} neu</span>
  }
  const zahl = (n: number | null) => n === null ? '–' : String(n)

  return (
    <>
      <header className={s.kopf}>
        <div>
          <h2 className={s.gruss}>{gruss()}</h2>
          <p className={s.zaehler}>
            <strong>{neu} neue {neu === 1 ? 'Eintrag' : 'Einträge'}</strong>
            {' '}seit gestern {uhr.format(daten.seit)} · {heuteFaellig} heute fällig · {ueberfaellig} überfällig
          </p>
        </div>
      </header>

      <Marktlage einschaetzung={daten.marktlage} laedt={laedt} />

      <div className={s.spalten}>
        <UebersichtSpalte titel="Positiv" ton="good" anzahl={daten.positiv.length}
                          nebenzahl={neuText(daten.positiv)}
                          leer={nurNeu ? 'Nichts Neues mit gutem Vorzeichen.' : 'Nichts mit gutem Vorzeichen in den letzten sieben Tagen.'}>
          {positiv.map((e) => <UebersichtEintrag key={e.key} eintrag={e} />)}
        </UebersichtSpalte>
        <UebersichtSpalte titel="Negativ" ton="bad" anzahl={daten.negativ.length}
                          nebenzahl={neuText(daten.negativ)}
                          leer={nurNeu ? 'Nichts Neues mit schlechtem Vorzeichen.' : 'Nichts mit schlechtem Vorzeichen in den letzten sieben Tagen.'}>
          {negativ.map((e) => <UebersichtEintrag key={e.key} eintrag={e} />)}
        </UebersichtSpalte>
        <UebersichtSpalte titel="Fällig" ton="neutral" anzahl={daten.faellig.length}
                          nebenzahl={<span className={s.ueberfaellig}>{ueberfaellig} überfällig</span>}
                          leer={nurNeu ? 'Keine neue Aufgabe, kein neuer Termin.' : 'Nichts fällig in den nächsten sieben Tagen.'}
                          fuss={<><Link to="/research">Alle Aufgaben</Link><Link to="/events">Kalender</Link></>}>
          {faellig.map((e) => <FaelligEintrag key={e.key} eintrag={e} />)}
        </UebersichtSpalte>
      </div>

      <section className={s.ohne}>
        <span className={s.label}>Ohne Vorzeichen</span>
        <span><strong>{zahl(daten.ohne.ideenVorgeschlagen)}</strong> Handelsideen vorgeschlagen</span>
        <span><strong>{zahl(daten.ohne.faelleStill)}</strong> Fälle ohne Aktualisierung seit mehr als 14 Tagen</span>
        <span><strong>{zahl(daten.ohne.amber)}</strong> Einschätzungen AMBER</span>
        <span className={s.schwellen}>Schwellen: Dip-Score ≥ {schwellen.dip} · Overhang ≥ {schwellen.overhang}</span>
      </section>
    </>
  )
}
